import {
  Download,
  Eye,
  File,
  FileText,
  Trash2,
  Upload,
  Image,
  Loader2,
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";


const DocumentManagerModal = ({ selectedRowData, config }) => {
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const IMAGE_PATH = `${config.SERVER_PATH}DOCUMENT_UPLOAD`;
  const DOC_TYPE = "PURCHASEINVOICEDOCUMENT";

  const [dragActive, setDragActive] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const docNo = selectedRowData?.SRVNO;

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${config.BASE_URL}/api/cpayscount/${COMPANY_CODE}/image/${docNo}/${DOC_TYPE}`
      );

      if (response.data && Array.isArray(response.data)) {
        const BASE_DOC_URL = `${config.DOC_GET_URL}/DOCUMENT_UPLOAD/`;

        const docs = response?.data?.map((item) => {
          const fileName = item?.Imagepath?.split("\\").pop();
          return {
            slno: item.slno,
            name: fileName,
            size: 0,
            url: `${BASE_DOC_URL}${fileName}`,
            type: fileName.split(".").pop().toLowerCase(),
            dateposted: item.dateposted,
          };
        });

        setDocuments(docs);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!docNo) return;
    fetchDocuments();
  }, [docNo]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
    }
  };

  const handleUpload = async (files) => {
    if (!description.trim()) {
      alert("Please enter a description before uploading.");
      return;
    }

    setUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("DOC_CODE", docNo);
        formData.append("DOC_TYPE", DOC_TYPE);
        formData.append("file", file);
        formData.append("cmpcode", config.COMPANY_CODE);
        formData.append("IMGBASE64", "XX");
        formData.append("IMAGEPATH", IMAGE_PATH);
        formData.append("DESCRIPTION", description.trim());

        await axios.post(
          `${config.IMAGE_POST_PATH}/api/Image/upload`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      }

      setDescription("");
      await fetchDocuments();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Failed to upload files");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (docToRemove) => {
    if (
      !window.confirm(`Are you sure you want to delete "${docToRemove.name}"?`)
    )
      return;

    try {
      const payload = [
        {
          CmpCode: config.COMPANY_CODE,
          mode: "DELETE",
          docuId: docToRemove.slno,
          comments: "-",
          user: config.CURRENT_USER,
        },
      ];

      const response = await axios.post(
        `${config.BASE_URL}/api/CRMImageDelete`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        await fetchDocuments();
      } else {
        alert("Failed to delete the document. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("An error occurred while deleting the document.");
    }
  };  

  const handleView = (doc) => {
    if (doc.url) window.open(doc.url, "_blank");
    else alert("Unable to open this file.");
  };

  const handleDownload = (doc) => {
    try {
      const link = document.createElement("a");
      link.href = doc.url;
      link.setAttribute("download", doc.name || "file");
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      alert("Failed to download file.");
    }
  };

  const getFileIcon = (fileName, type) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (ext === "pdf") return <FileText className="w-4 h-4 text-red-500" />;
    if (["xlsx", "xls"].includes(ext))
      return <File className="w-4 h-4 text-green-600" />;
    if (["jpg", "jpeg", "png", "gif"].includes(ext))
      return <Image className="w-4 h-4 text-purple-500" />;
    return <FileText className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className=" h-screen flex flex-col bg-gray-50">
      {/* Document List - Primary Focus */}
      <div className="px-4 pt-4 pb-2">
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg transition-all ${
            dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-white"
          }`}
        >
          <div className="p-4">
            {/* Compact Header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Upload className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">
                  Upload New Document
                </h3>
              </div>
            </div>

            {/* Inline Description + Upload */}
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description (required)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  disabled={uploading}
                />
              </div>
              <div>
                <input
                  type="file"
                  multiple
                  onChange={handleFileInput}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.xlsx,.xls,.jpg,.jpeg,.png"
                  disabled={!description.trim() || uploading}
                />
                <label
                  htmlFor="file-upload"
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                    uploading
                      ? "bg-blue-400 text-white cursor-wait"
                      : !description.trim()
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                  }`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-1.5" />
                      Browse
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Helper text */}
            <p className="text-xs text-gray-500 mt-2">
              PDF, Excel, or Images •{" "}
              {!description.trim() && (
                <span className="text-amber-600 font-medium">
                  Description required
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-hidden px-4 pb-2">
        <div className="bg-white rounded-lg border border-gray-200 h-full flex flex-col shadow-sm">
          {/* List Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-t-lg">
            <h4 className="text-sm font-semibold text-gray-800">
              Documents ({documents.length})
            </h4>
          </div>

          {/* List Content */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <p className="text-sm">Loading documents...</p>
                </div>
              </div>
            ) : documents.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium text-gray-600">
                    No documents uploaded yet
                  </p>
                  <p className="text-xs mt-1 text-gray-500">
                    Upload files using the form below
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-full overflow-y-auto p-3">
                <div className="space-y-1.5">
                  {documents.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2.5 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100 group"
                    >
                      <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                        {getFileIcon(doc.name, doc.type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {doc.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {doc.dateposted
                              ? new Date(doc.dateposted).toLocaleString()
                              : ""}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-0.5">
                        <button
                          onClick={() => handleView(doc)}
                          className="p-1.5 hover:bg-blue-50 rounded transition-colors"
                          title="View"
                        >
                          <Eye className="w-4 h-4 text-gray-500 hover:text-blue-600" />
                        </button>

                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-1.5 hover:bg-green-50 rounded transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4 text-gray-500 hover:text-green-600" />
                        </button>

                        <button
                          onClick={() => handleRemove(doc)}
                          className="p-1.5 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentManagerModal;
